<script setup>
/**
 * HelpTooltip — Inline help icon with hover/click tooltip for contextual documentation.
 * 
 * Usage:
 *   <HelpTooltip text="Campaigns group related events together for tracking and reporting." />
 *   <HelpTooltip text="Capacity includes base + buffer seats.">
 *     <template #icon>
 *       <InformationCircleIcon class="h-4 w-4" />
 *     </template>
 *   </HelpTooltip>
 */
import { ref, onMounted, onBeforeUnmount } from 'vue';
import { QuestionMarkCircleIcon } from '@heroicons/vue/20/solid';

const props = defineProps({
  text: {
    type: String,
    required: true
  },
  position: {
    type: String,
    default: 'top',
    validator: (v) => ['top', 'bottom', 'left', 'right'].includes(v)
  },
  maxWidth: {
    type: String,
    default: '16rem'
  }
});

const isVisible = ref(false);
const tooltipRef = ref(null);

function show() { isVisible.value = true; }
function hide() { isVisible.value = false; }
function toggle() { isVisible.value = !isVisible.value; }

// Close on click outside
function handleClickOutside(e) {
  if (tooltipRef.value && !tooltipRef.value.contains(e.target)) {
    isVisible.value = false;
  }
}

onMounted(() => {
  document.addEventListener('click', handleClickOutside);
});

onBeforeUnmount(() => {
  document.removeEventListener('click', handleClickOutside);
});

const positionClasses = {
  top: 'bottom-full left-1/2 -translate-x-1/2 mb-2',
  bottom: 'top-full left-1/2 -translate-x-1/2 mt-2',
  left: 'right-full top-1/2 -translate-y-1/2 mr-2',
  right: 'left-full top-1/2 -translate-y-1/2 ml-2',
};

const arrowClasses = {
  top: 'top-full left-1/2 -translate-x-1/2 border-t-gray-800 border-x-transparent border-b-transparent border-4',
  bottom: 'bottom-full left-1/2 -translate-x-1/2 border-b-gray-800 border-x-transparent border-t-transparent border-4',
  left: 'left-full top-1/2 -translate-y-1/2 border-l-gray-800 border-y-transparent border-r-transparent border-4',
  right: 'right-full top-1/2 -translate-y-1/2 border-r-gray-800 border-y-transparent border-l-transparent border-4',
};
</script>

<template>
  <span
    ref="tooltipRef"
    class="relative inline-flex items-center"
    @mouseenter="show"
    @mouseleave="hide"
    @click.stop="toggle"
  >
    <slot name="icon">
      <QuestionMarkCircleIcon class="h-4 w-4 text-gray-400 hover:text-gray-600 cursor-help transition-colors" />
    </slot>
    <Transition
      enter-active-class="transition ease-out duration-150"
      enter-from-class="opacity-0 scale-95"
      enter-to-class="opacity-100 scale-100"
      leave-active-class="transition ease-in duration-100"
      leave-from-class="opacity-100 scale-100"
      leave-to-class="opacity-0 scale-95"
    >
      <span
        v-if="isVisible"
        :class="positionClasses[position]"
        :style="{ maxWidth: maxWidth }"
        class="absolute z-50 rounded-md bg-gray-800 px-3 py-2 text-xs text-white shadow-lg whitespace-normal text-left font-normal leading-relaxed"
        role="tooltip"
      >
        {{ text }}
        <span :class="arrowClasses[position]" class="absolute w-0 h-0" />
      </span>
    </Transition>
  </span>
</template>
